import { IEvent, ILocation, IWeeklyDeclaration, IMessageCategory, IMessageOfTheDay, IMessageOfTheDayFeed, IMinistryJoinRequest, IPrayerRequest, ITestimony, IResponse, IAmenClickStats, IHeroSlide, IAnnouncement, IMediaFile, ISermon, IChapterContent, IChapterGivingAccount } from "@dc-ireland/shared"

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001"

type ApiListResponse<T> = { success?: boolean; count?: number; data?: T }
type ApiDataResponse<T> = { success?: boolean; data?: T }

type CloudinaryUploadConfig = {
    cloudName: string
    uploadPreset: string
    folder: string
}

const MAX_UPLOAD_FILES = 50
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024
const allowedUploadName = /\.(jpe?g|png|gif|webp|pdf|docx?)$/i

const assertAllowedUpload = (file: File) => {
    if (!allowedUploadName.test(file.name)) {
        throw new Error(`${file.name}: only images and documents can be uploaded`)
    }
    if (file.size > MAX_UPLOAD_BYTES) {
        throw new Error(`${file.name}: file is larger than 10MB`)
    }
}

class ApiClient {
    private baseURL: string
    private cloudinaryUploadConfig: CloudinaryUploadConfig | null | undefined

    constructor(baseURL: string) {
        this.baseURL = baseURL
    }

    private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        const url = `${this.baseURL}${endpoint}`
        const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData

        const config: RequestInit = {
            headers: {
                ...(isFormData ? {} : { "Content-Type": "application/json" }),
                ...options.headers,
            },
            ...options,
        }

        const token = localStorage.getItem("auth_token")
        if (token) {
            config.headers = {
                ...config.headers,
                Authorization: `Bearer ${token}`,
            }
        }

        try {
            const response = await fetch(url, config)
            const text = await response.text()

            if (!response.ok) {
                let message = `HTTP error! status: ${response.status}`
                try {
                    const body = text ? JSON.parse(text) : null
                    if (body && typeof body.error === "string") message = body.error
                } catch {
                    if (text && text.length < 200) message = text
                }
                throw new Error(message)
            }

            if (!text) throw new Error("Empty response from server")
            try {
                return JSON.parse(text) as T
            } catch {
                throw new Error("Invalid JSON response from server")
            }
        } catch (error) {
            console.error("API request failed:", error)
            throw error
        }
    }

    async getEvents(chapter?: string): Promise<IEvent[]> {
        const query = chapter ? `?chapter=${encodeURIComponent(chapter)}` : ""
        const res = await this.request<ApiListResponse<IEvent[]>>(`/api/events${query}`)
        const data = res?.data
        return Array.isArray(data) ? data : []
    }

    async createEvent(event: Omit<IEvent, "id">): Promise<IEvent> {
        const res = await this.request<ApiDataResponse<IEvent>>("/api/events", {
            method: "POST",
            body: JSON.stringify(event),
        })
        if (!res?.data) throw new Error("Failed to create event")
        return res.data
    }

    async updateEvent(id: string, event: Partial<IEvent>): Promise<IEvent> {
        const res = await this.request<ApiDataResponse<IEvent>>(`/api/events/${id}`, {
            method: "PUT",
            body: JSON.stringify(event),
        })
        if (!res?.data) throw new Error("Failed to update event")
        return res.data
    }

    async deleteEvent(id: string): Promise<void> {
        await this.request<{ success?: boolean; message?: string }>(`/api/events/${id}`, {
            method: "DELETE",
        })
    }

    async getWeeklyDeclaration(): Promise<IWeeklyDeclaration | null> {
        try {
            const res = await this.request<ApiDataResponse<IWeeklyDeclaration>>("/api/weekly-declaration")
            return res?.data ?? null
        } catch {
            return null
        }
    }

    async updateWeeklyDeclaration(declaration: Omit<IWeeklyDeclaration, "id">): Promise<IWeeklyDeclaration> {
        const res = await this.request<ApiDataResponse<IWeeklyDeclaration>>("/api/weekly-declaration", {
            method: "PUT",
            body: JSON.stringify(declaration),
        })
        if (!res?.data) throw new Error("Failed to update weekly declaration")
        return res.data
    }

    async getMessageCategories(): Promise<IMessageCategory[]> {
        try {
            const res = await this.request<ApiListResponse<IMessageCategory[]>>(
                "/api/message-of-the-day/categories"
            )
            const data = res?.data
            return Array.isArray(data) ? data : []
        } catch {
            return []
        }
    }

    async createMessageCategory(category: Omit<IMessageCategory, "id" | "messageCount">): Promise<IMessageCategory> {
        const res = await this.request<ApiDataResponse<IMessageCategory>>("/api/message-of-the-day/categories", {
            method: "POST",
            body: JSON.stringify(category),
        })
        if (!res?.data) throw new Error("Failed to create category")
        return res.data
    }

    async getMessageCategory(slug: string, includeAllMessages = false): Promise<IMessageOfTheDayFeed | null> {
        try {
            const query = includeAllMessages ? "?all=true" : ""
            const res = await this.request<ApiDataResponse<IMessageOfTheDayFeed>>(
                `/api/message-of-the-day/categories/${slug}${query}`
            )
            return res?.data ?? null
        } catch {
            return null
        }
    }

    async updateMessageCategory(
        slug: string,
        category: Partial<Pick<IMessageCategory, "name" | "backgroundUrl" | "sortOrder">>
    ): Promise<IMessageOfTheDayFeed> {
        const res = await this.request<ApiDataResponse<IMessageOfTheDayFeed>>(
            `/api/message-of-the-day/categories/${slug}`,
            {
                method: "PUT",
                body: JSON.stringify(category),
            }
        )
        if (!res?.data) throw new Error("Failed to update category")
        return res.data
    }

    async deleteMessageCategory(slug: string): Promise<void> {
        await this.request<{ success?: boolean }>(`/api/message-of-the-day/categories/${slug}`, {
            method: "DELETE",
        })
    }

    async createMessageOfTheDay(
        categorySlug: string,
        message: Omit<IMessageOfTheDay, "id" | "categorySlug">
    ): Promise<IMessageOfTheDay> {
        const res = await this.request<ApiDataResponse<IMessageOfTheDay>>(
            `/api/message-of-the-day/categories/${categorySlug}/messages`,
            {
                method: "POST",
                body: JSON.stringify(message),
            }
        )
        if (!res?.data) throw new Error("Failed to create message")
        return res.data
    }

    async updateMessageOfTheDayEntry(
        id: string,
        message: Partial<Omit<IMessageOfTheDay, "id" | "categorySlug">>
    ): Promise<IMessageOfTheDay> {
        const res = await this.request<ApiDataResponse<IMessageOfTheDay>>(
            `/api/message-of-the-day/messages/${id}`,
            {
                method: "PUT",
                body: JSON.stringify(message),
            }
        )
        if (!res?.data) throw new Error("Failed to update message")
        return res.data
    }

    async deleteMessageOfTheDayEntry(id: string): Promise<void> {
        await this.request<{ success?: boolean }>(`/api/message-of-the-day/messages/${id}`, {
            method: "DELETE",
        })
    }

    async getMessageOfTheDay(categorySlug = "chronicles-of-love"): Promise<IMessageOfTheDayFeed | null> {
        try {
            const res = await this.request<ApiDataResponse<IMessageOfTheDayFeed>>(
                `/api/message-of-the-day/${categorySlug}`
            )
            return res?.data ?? null
        } catch {
            return null
        }
    }

    async createPrayerRequest(request: Omit<IPrayerRequest, "id" | "createdAt">): Promise<IResponse> {
        return this.request<IResponse>("/api/prayer-requests", {
            method: "POST",
            body: JSON.stringify(request),
        })
    }

    async getPrayerRequests(): Promise<IPrayerRequest[]> {
        const res = await this.request<ApiListResponse<IPrayerRequest[]>>("/api/prayer-requests")
        const data = res?.data
        return Array.isArray(data) ? data : []
    }

    async deletePrayerRequest(id: string): Promise<void> {
        await this.request<{ success?: boolean }>(`/api/prayer-requests/${id}`, {
            method: "DELETE",
        })
    }

    async getPrayerRequestStats(): Promise<{ total: number; recent: number }> {
        const res = await this.request<ApiDataResponse<{ total: number; recent: number }>>(
            "/api/prayer-requests/stats/overview"
        )
        return res?.data ?? { total: 0, recent: 0 }
    }

    async createTestimony(request: Omit<ITestimony, "id" | "createdAt">): Promise<IResponse> {
        return this.request<IResponse>("/api/testimonies", {
            method: "POST",
            body: JSON.stringify(request),
        })
    }

    async getTestimonies(): Promise<ITestimony[]> {
        const res = await this.request<ApiListResponse<ITestimony[]>>("/api/testimonies")
        const data = res?.data
        return Array.isArray(data) ? data : []
    }

    async deleteTestimony(id: string): Promise<void> {
        await this.request<{ success?: boolean }>(`/api/testimonies/${id}`, {
            method: "DELETE",
        })
    }

    async getTestimonyStats(): Promise<{ total: number; recent: number }> {
        const res = await this.request<ApiDataResponse<{ total: number; recent: number }>>(
            "/api/testimonies/stats/overview"
        )
        return res?.data ?? { total: 0, recent: 0 }
    }

    async createMinistryJoinRequest(
        request: Omit<IMinistryJoinRequest, "id" | "createdAt">
    ): Promise<IResponse> {
        return this.request<IResponse>("/api/ministry-join-requests", {
            method: "POST",
            body: JSON.stringify(request),
        })
    }

    async createCellGroupInterest(request: {
        cellGroupId: string
        name: string
        channel: "whatsapp" | "sms"
    }): Promise<{ whatsappUrl: string; smsUrl: string }> {
        const res = await this.request<ApiDataResponse<{ whatsappUrl: string; smsUrl: string }>>(
            "/api/cell-group-interest",
            {
                method: "POST",
                body: JSON.stringify(request),
            }
        )
        if (!res?.data?.whatsappUrl || !res?.data?.smsUrl) {
            throw new Error("Failed to create cell group request")
        }
        return res.data
    }

    async trackAmenClick(category = "weekly-declaration"): Promise<void> {
        try {
            await this.request<{ success?: boolean }>("/api/amen-clicks", {
                method: "POST",
                body: JSON.stringify({ category }),
            })
        } catch (error) {
            console.error("Failed to track amen click:", error)
        }
    }

    async getAmenClickStats(): Promise<IAmenClickStats> {
        const res = await this.request<ApiDataResponse<IAmenClickStats>>("/api/amen-clicks/stats/overview")
        return res?.data ?? { total: 0, today: 0, last7Days: 0, last30Days: 0 }
    }

    async getLocations(): Promise<ILocation[]> {
        const res = await this.request<ApiListResponse<ILocation[]>>("/api/locations")
        const data = res?.data
        return Array.isArray(data) ? data : []
    }

    async getChapterContent(chapterSlug: string): Promise<IChapterContent> {
        const res = await this.request<ApiDataResponse<IChapterContent>>(`/api/chapters/${encodeURIComponent(chapterSlug)}/content`)
        const data = res?.data
        return {
            gallery: Array.isArray(data?.gallery) ? data.gallery : [],
            cellGroups: Array.isArray(data?.cellGroups) ? data.cellGroups : [],
            birthdays: Array.isArray(data?.birthdays) ? data.birthdays : [],
        }
    }

    async getGivingAccounts(includeDisabled = false): Promise<IChapterGivingAccount[]> {
        const query = includeDisabled ? "?all=true" : ""
        const res = await this.request<ApiListResponse<IChapterGivingAccount[]>>(`/api/giving${query}`)
        const data = res?.data
        return Array.isArray(data) ? data : []
    }

    async createGivingAccount(
        account: Omit<IChapterGivingAccount, "id">
    ): Promise<IChapterGivingAccount> {
        const res = await this.request<ApiDataResponse<IChapterGivingAccount>>("/api/giving", {
            method: "POST",
            body: JSON.stringify(account),
        })
        if (!res?.data) throw new Error("Failed to create giving account")
        return res.data
    }

    async updateGivingAccount(
        chapterSlug: string,
        account: Partial<IChapterGivingAccount>
    ): Promise<IChapterGivingAccount> {
        const res = await this.request<ApiDataResponse<IChapterGivingAccount>>(
            `/api/giving/${encodeURIComponent(chapterSlug)}`,
            {
                method: "PUT",
                body: JSON.stringify(account),
            }
        )
        if (!res?.data) throw new Error("Failed to update giving account")
        return res.data
    }

    async deleteGivingAccount(chapterSlug: string): Promise<void> {
        await this.request<{ success?: boolean }>(`/api/giving/${encodeURIComponent(chapterSlug)}`, {
            method: "DELETE",
        })
    }

    async createLocation(location: Omit<ILocation, "id">): Promise<ILocation> {
        const res = await this.request<ApiDataResponse<ILocation>>("/api/locations", {
            method: "POST",
            body: JSON.stringify(location),
        })
        if (!res?.data) throw new Error("Failed to create location")
        return res.data
    }

    async updateLocation(slug: string, location: Partial<ILocation>): Promise<ILocation> {
        const res = await this.request<ApiDataResponse<ILocation>>(`/api/locations/${slug}`, {
            method: "PUT",
            body: JSON.stringify(location),
        })
        if (!res?.data) throw new Error("Failed to update location")
        return res.data
    }

    async deleteLocation(slug: string): Promise<void> {
        await this.request<{ success?: boolean }>(`/api/locations/${slug}`, {
            method: "DELETE",
        })
    }

    async getHeroSlides(includeDisabled = false): Promise<IHeroSlide[]> {
        try {
            const query = includeDisabled ? "?all=true" : ""
            const res = await this.request<ApiListResponse<IHeroSlide[]>>(`/api/hero-slides${query}`)
            const data = res?.data
            return Array.isArray(data) ? data : []
        } catch {
            return []
        }
    }

    async updateHeroSlides(slides: IHeroSlide[]): Promise<IHeroSlide[]> {
        const res = await this.request<ApiListResponse<IHeroSlide[]>>("/api/hero-slides", {
            method: "PUT",
            body: JSON.stringify({ slides }),
        })
        return Array.isArray(res?.data) ? res.data : []
    }

    async getAnnouncements(includeDisabled = false, chapter?: string): Promise<IAnnouncement[]> {
        try {
            const params = new URLSearchParams()
            if (includeDisabled) params.set("all", "true")
            if (chapter) params.set("chapter", chapter)
            const query = params.toString() ? `?${params.toString()}` : ""
            const res = await this.request<ApiListResponse<IAnnouncement[]>>(`/api/announcements${query}`)
            const data = res?.data
            return Array.isArray(data) ? data : []
        } catch {
            return []
        }
    }

    async updateAnnouncements(announcements: IAnnouncement[]): Promise<IResponse> {
        const res = await this.request<{ success?: boolean; message?: string }>("/api/announcements", {
            method: "PUT",
            body: JSON.stringify({ announcements }),
        })
        return { message: res.message ?? "Updated", status: "ok" }
    }

    async getSermons(includeDisabled = false): Promise<ISermon[]> {
        try {
            const query = includeDisabled ? "?all=true" : ""
            const res = await this.request<ApiListResponse<ISermon[]>>(`/api/sermons${query}`)
            const data = res?.data
            return Array.isArray(data) ? data : []
        } catch {
            return []
        }
    }

    async updateSermons(sermons: ISermon[]): Promise<ISermon[]> {
        const res = await this.request<ApiListResponse<ISermon[]>>("/api/sermons", {
            method: "PUT",
            body: JSON.stringify({ sermons }),
        })
        return Array.isArray(res?.data) ? res.data : []
    }

    async login(
        email: string,
        password: string
    ): Promise<{
        token: string
        user: { id: string; email: string }
        scope?: "chapter-intake" | "admin"
        chapterSlug?: string
        expiresAt?: string
    }> {
        return this.request("/api/auth/login", {
            method: "POST",
            body: JSON.stringify({ email, password }),
        })
    }

    async registerAdmin(email: string, password: string): Promise<{ user: { id: string; email: string } }> {
        return this.request("/api/auth/register", {
            method: "POST",
            body: JSON.stringify({ email, password }),
        })
    }

    async logout(): Promise<void> {
        return this.request<void>("/api/auth/logout", {
            method: "POST",
        })
    }

    async getCurrentUser(): Promise<{ user: { id: string; email: string } }> {
        return this.request<{ user: { id: string; email: string } }>("/api/auth/me")
    }

    private async getCloudinaryUploadConfig(): Promise<CloudinaryUploadConfig | null> {
        if (this.cloudinaryUploadConfig !== undefined) {
            return this.cloudinaryUploadConfig
        }

        try {
            const res = await this.request<ApiDataResponse<CloudinaryUploadConfig | null>>(
                "/api/upload/cloudinary-config"
            )
            this.cloudinaryUploadConfig = res?.data ?? null
        } catch {
            this.cloudinaryUploadConfig = null
        }

        return this.cloudinaryUploadConfig
    }

    private async registerMediaFile(payload: {
        name: string
        url: string
        publicId?: string
        mimeType?: string
        size?: number
    }): Promise<IMediaFile> {
        const res = await this.request<ApiDataResponse<IMediaFile>>("/api/upload/register", {
            method: "POST",
            body: JSON.stringify(payload),
        })
        if (!res?.data?.url) throw new Error("Failed to register uploaded file")
        return res.data
    }

    async uploadFile(file: File, name?: string): Promise<IMediaFile> {
        assertAllowedUpload(file)
        const displayName = (name?.trim() || file.name.replace(/\.[^/.]+$/, "")).slice(0, 200)
        const cloudinaryConfig = await this.getCloudinaryUploadConfig()

        if (cloudinaryConfig) {
            const formData = new FormData()
            formData.append("file", file)
            formData.append("upload_preset", cloudinaryConfig.uploadPreset)

            const response = await fetch(
                `https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloudName}/auto/upload`,
                {
                    method: "POST",
                    body: formData,
                }
            )

            const body = (await response.json()) as {
                secure_url?: string
                public_id?: string
                bytes?: number
                error?: { message?: string }
            }

            if (!response.ok || !body.secure_url || !body.public_id) {
                throw new Error(body.error?.message || "Cloudinary upload failed")
            }

            return this.registerMediaFile({
                name: displayName,
                url: body.secure_url,
                publicId: body.public_id,
                mimeType: file.type || undefined,
                size: body.bytes ?? file.size,
            })
        }

        const formData = new FormData()
        formData.append("file", file)
        if (name) formData.append("name", name)

        const res = await this.request<ApiDataResponse<IMediaFile>>("/api/upload", {
            method: "POST",
            body: formData,
        })
        if (!res?.data?.url) throw new Error("Upload failed")
        return res.data
    }

    async uploadFiles(
        files: File[],
        options?: { name?: string; onProgress?: (completed: number, total: number) => void }
    ): Promise<IMediaFile[]> {
        if (files.length > MAX_UPLOAD_FILES) {
            throw new Error(`You can upload up to ${MAX_UPLOAD_FILES} files at a time.`)
        }

        const uploaded: IMediaFile[] = []
        const failures: string[] = []

        for (let index = 0; index < files.length; index++) {
            const file = files[index]
            const displayName = files.length === 1 ? options?.name : undefined
            try {
                uploaded.push(await this.uploadFile(file, displayName))
            } catch (err) {
                const message = err instanceof Error ? err.message : "Upload failed"
                failures.push(`${file.name}: ${message}`)
            }
            options?.onProgress?.(index + 1, files.length)
        }

        if (failures.length > 0) {
            const uploadedNote = uploaded.length > 0 ? ` ${uploaded.length} file(s) were uploaded.` : ""
            throw new Error(`${failures.join(" ")} ${uploadedNote}`.trim())
        }

        return uploaded
    }

    async getMediaFiles(): Promise<IMediaFile[]> {
        const res = await this.request<ApiListResponse<IMediaFile[]>>("/api/upload")
        const data = res?.data
        return Array.isArray(data) ? data : []
    }

    async syncMediaFromCloudinary(): Promise<{
        folder: string
        found: number
        imported: number
        skipped: number
    }> {
        const res = await this.request<
            ApiDataResponse<{
                folder: string
                found: number
                imported: number
                skipped: number
            }> & { message?: string }
        >("/api/upload/sync-cloudinary", {
            method: "POST",
        })
        if (!res?.data) throw new Error("Failed to sync from Cloudinary")
        return res.data
    }

    async renameMediaFile(id: string, name: string): Promise<IMediaFile> {
        const res = await this.request<ApiDataResponse<IMediaFile>>(`/api/upload/${id}`, {
            method: "PATCH",
            body: JSON.stringify({ name }),
        })
        if (!res?.data) throw new Error("Failed to rename file")
        return res.data
    }

    async deleteMediaFile(id: string): Promise<void> {
        await this.request<{ success?: boolean }>(`/api/upload/${id}`, {
            method: "DELETE",
        })
    }

    async claimChapterIntakeAccess(token: string): Promise<{
        token: string
        scope: "chapter-intake"
        chapterSlug: string
        contactEmail: string
        accessEmail: string
        expiresAt: string
        uploadUrl: string
    }> {
        return this.request("/api/chapter-intake/claim", {
            method: "POST",
            body: JSON.stringify({ token }),
        })
    }

    async getChapterIntakeSession(): Promise<{
        scope: "chapter-intake"
        chapterSlug: string
        contactEmail: string
        accessEmail: string
        expiresAt: string
        uploadCount: number
        maxUploads: number
    }> {
        const res = await this.request<
            ApiDataResponse<{
                scope: "chapter-intake"
                chapterSlug: string
                contactEmail: string
                accessEmail: string
                expiresAt: string
                uploadCount: number
                maxUploads: number
            }>
        >("/api/chapter-intake/me")
        if (!res?.data) throw new Error("Upload session expired")
        return res.data
    }

    async uploadChapterIntakeAssets(
        assets: Array<{ file: File; type: string; name: string }>
    ): Promise<{ chapterSlug: string; contactEmail: string; uploads: Array<{ type: string; name: string; url: string; publicId: string }> }> {
        const formData = new FormData()
        assets.forEach((asset, index) => {
            formData.append("files", asset.file)
            formData.append(`assets[${index}][type]`, asset.type)
            formData.append(`assets[${index}][name]`, asset.name)
        })

        const res = await this.request<
            ApiDataResponse<{
                chapterSlug: string
                contactEmail: string
                uploads: Array<{ type: string; name: string; url: string; publicId: string }>
            }>
        >("/api/chapter-intake/assets", {
            method: "POST",
            body: formData,
        })

        if (!res?.data) throw new Error("Upload failed")
        return res.data
    }
}

export const apiClient = new ApiClient(API_BASE_URL)
export default apiClient
